import { Mollie } from "@omni/types/packages/mollie.types";
import { isDefined } from "@omni/utils/helper.utils";
import React, { Context, use, useEffect, useState } from "react";

import { Config } from "@/utils/config.utils";
import { ScriptLoadingStates, useScript } from "@/utils/hooks/useScript.hook";

export type MollieContextValues = {
    mollie?: Mollie;
};

const defaultContext: MollieContextValues = {
    mollie: undefined
};

const MollieContext: Context<MollieContextValues> =
    React.createContext<MollieContextValues>(defaultContext);

export type MollieProviderProps = {
    children?: React.ReactNode;
};

// Save Mollie outside hook to initialize only one Mollie instance even if hook is rendered in multiple components
let mollieInstance: Mollie | undefined;
const getMollie = (mollieConstructor: Window["Mollie"]) => {
    if (isDefined(mollieInstance)) return mollieInstance;

    mollieInstance = mollieConstructor(
        Config.integrations["mollie-profile-id"],
        {
            locale: Config.getLocale(),
            testmode: Config.integrations["mollie-test-mode"]
        }
    );

    return mollieInstance;
};

const MollieProvider = ({ children }: MollieProviderProps) => {
    const { status: mollieScriptStatus } = useScript({
        src: "https://js.mollie.com/v1/mollie.js",
        id: "mollie"
    });
    const [mollie, setMollie] = useState<Mollie | undefined>(undefined);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (mollieScriptStatus !== ScriptLoadingStates.Ready) return;
        if (isDefined(mollie)) return;

        setMollie(() => getMollie(window.Mollie));
    }, [mollieScriptStatus, mollie, setMollie]);

    if (mollieScriptStatus === ScriptLoadingStates.Ready && mollie) {
        return (
            <MollieContext value={{ mollie: mollie }}>{children}</MollieContext>
        );
    }

    return null;
};

const useMollie = (): MollieContextValues => {
    const context = use<MollieContextValues>(MollieContext);
    if (context === undefined) {
        throw new Error("MollieContext is undefined.");
    }
    return context;
};

export { MollieProvider, useMollie };
